import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { AccountTypeahead } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { CompanyShare, CompanyShareBasic } from '~hooks/useCompanyShares/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import { AccountExternalTruckIdentifier } from '~hooks/useEquipment/models';
import { useAccountExternalTruckIdentifier } from '~hooks/useEquipment/useAccountExternalTruckIdentifier';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import { Pagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';
import { getPageNumber } from '~utils/pagination';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const getCompanyShareLabel = (companyShare?: CompanyShare) => {
  if (!companyShare) {
    return '';
  }

  const equipmentName = companyShare.companyShareable?.name || '';
  const externalId = companyShare.externalId;

  if (externalId) {
    return `${externalId} (${equipmentName})`;
  }

  return equipmentName;
};

interface CompanyShareSelectorCellProps {
  defaultValue: Nullable<CompanyShareBasic>;
  onSelect: (share: Nullable<CompanyShare>) => void;
}

const CompanyShareSelectorCell = observer(
  ({ defaultValue, onSelect }: CompanyShareSelectorCellProps) => {
    const { companyAssetsStore } = useStores();

    const { control, formState, reset } = useForm({
      defaultValues: {
        companyShare: defaultValue,
      },
    });

    useEffect(() => {
      reset({
        companyShare: defaultValue,
      });
    }, [defaultValue, reset]);

    return (
      <Box sx={{ maxWidth: 300, width: '100%' }}>
        <AutocompleteFormField
          control={control}
          errors={formState.errors}
          name="companyShare"
          list={companyAssetsStore.companyShares}
          getValue={(item) => item?.id || ''}
          getLabel={(item: CompanyShare) => getCompanyShareLabel(item)}
          disablePortal={false}
          inputProps={{
            sx: {
              '& .MuiInputBase-input': {
                fontSize: '11px',
              },
            },
          }}
          onSelect={onSelect}
          placeholder={t('form_fields.select_truck')}
          clearable
        />
      </Box>
    );
  },
);

const IDMatching = observer(() => {
  const { userStore, companyAssetsStore } = useStores();
  const { getAccountsByCompanyIdTypeahead } = useAccount();
  const { getAllCompanyShares, isLoading: isLoadingShares } = useCompanyShares();
  const {
    getAccountExternalTruckIdentifiersByCompanyId,
    updateAccountExternalTruckIdentifier,
  } = useAccountExternalTruckIdentifier();
  const companyId = getEffectiveUserCompanyId(userStore);

  const form = useForm({
    defaultValues: {
      account: null as AccountTypeahead | null,
    },
  });

  const fetchAccountsByCompanyId = useCallback(
    async (additionalProps = {}) => {
      if (!companyId) {
        return [];
      }

      return await getAccountsByCompanyIdTypeahead({
        companyId,
        ...additionalProps,
      });
    },
    [companyId, getAccountsByCompanyIdTypeahead],
  );

  const selectedAccount = form.watch('account');
  const prevCompanyIdRef = useRef(companyId);
  const [rows, setRows] = useState<AccountExternalTruckIdentifier[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [isLoadingIdentifiers, setIsLoadingIdentifiers] = useState(false);
  const issueIdentifiersRequestSignal = useLatestRequestSignal();

  const fetchIdentifiersPage = async (
    accountId: string,
    link?: PaginationLink,
    cursor?: string,
  ) => {
    const signal = issueIdentifiersRequestSignal();
    setIsLoadingIdentifiers(true);

    try {
      const result = await getAccountExternalTruckIdentifiersByCompanyId({
        accountId,
        companyId,
        cursor,
        link,
        signal,
      });
      if (signal.aborted) {
        return;
      }

      setRows(result.data);
      setPagination((previousPagination) => ({
        ...result.pagination,
        page: getPageNumber(previousPagination?.page ?? 1, link),
      }));
    } catch {
      // Error toast is handled in the hook.
    } finally {
      if (!signal.aborted) {
        setIsLoadingIdentifiers(false);
      }
    }
  };

  useEffect(() => {
    // aborts the previous account's request, which would otherwise refill the grid
    issueIdentifiersRequestSignal();
    setIsLoadingIdentifiers(false);
    setRows([]);
    setPagination(undefined);

    if (prevCompanyIdRef.current !== companyId) {
      // reset account when company changed from switcher
      form.setValue('account', null);
      companyAssetsStore.setCompanyShares([]);
      prevCompanyIdRef.current = companyId;
      return;
    }

    if (companyId && selectedAccount?.id) {
      void fetchIdentifiersPage(selectedAccount.id);
    }
  }, [companyId, selectedAccount?.id]);

  const firstIdentifierRow = rows[0];
  const receiverCompanyId = firstIdentifierRow?.vendorAccount?.accountBasicCompanyId;
  const senderCompanyId = firstIdentifierRow?.customerAccount?.accountBasicCompanyId;

  // Fetch company shares when we have the derived company IDs
  useEffect(() => {
    if (!companyId || !selectedAccount?.id || !receiverCompanyId || !senderCompanyId) {
      companyAssetsStore.setCompanyShares([]);
      return;
    }

    getAllCompanyShares({
      companyId,
      receiver_company_id: receiverCompanyId,
      sender_company_id: senderCompanyId,
    });
  }, [companyId, selectedAccount?.id, receiverCompanyId, senderCompanyId]);

  const handleCompanyShareChange = useCallback(
    async (truckIdentifierId: string, companyShare: Nullable<CompanyShare>) => {
      if (!companyId) {
        return;
      }
      const updatedIdentifier = await updateAccountExternalTruckIdentifier({
        companyId,
        truckIdentifierId,
        companyShareId: companyShare?.id || null,
      });
      setRows((previousRows) =>
        previousRows.map((row) =>
          row.id === updatedIdentifier.id ? updatedIdentifier : row,
        ),
      );
    },
    [companyId, updateAccountExternalTruckIdentifier],
  );

  const handleNavigateIdentifiersPage = (link?: PaginationLink) => {
    if (!selectedAccount?.id) {
      return;
    }

    void fetchIdentifiersPage(
      selectedAccount.id,
      link,
      link ? pagination?.[link] : undefined,
    );
  };

  const columns = useMemo<Array<DataTableColumn<AccountExternalTruckIdentifier>>>(
    () => [
      {
        field: 'externalTruckId',
        headerName: t('administration.equipment.ticket_match_id'),
        valueGetter: (row) => row.externalTruckId,
      },
      {
        field: 'companyShare',
        headerName: t('administration.equipment.tread_equipment_matching'),
        renderCell: (row) => (
          <CompanyShareSelectorCell
            defaultValue={row.companyShare}
            onSelect={(share) => handleCompanyShareChange(row.id, share)}
          />
        ),
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        valueGetter: (row) => dateFormat(row.createdAt),
      },
      {
        field: 'lastTicketReceivedAt',
        headerName: t('common.last_ticket'),
        valueGetter: (row) =>
          row.lastTicketReceivedAt ? dateFormat(new Date(row.lastTicketReceivedAt)) : '',
      },
    ],
    [handleCompanyShareChange],
  );

  if (!companyId) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body1" color="error">
          {t('error_messages.no_company_selected')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ maxWidth: 400, mb: 3 }}>
        <AutocompleteAsyncFormField
          asyncCallback={fetchAccountsByCompanyId}
          control={form.control}
          errors={form.formState.errors}
          getLabel={(item) => item.name || ''}
          getValue={(item) => item.id}
          label={t('administration.equipment.select_account')}
          name="account"
          clearable
        />
      </Box>
      {!selectedAccount ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('administration.equipment.select_account_to_view_identifiers')}
        </Typography>
      ) : (
        <Box data-test-id={'id-matching-data-grid'}>
          <DataTable
            id="id-matching-data-grid"
            columns={columns}
            rows={rows}
            loading={isLoadingIdentifiers || isLoadingShares}
            headerTrailingComponent={
              <DataGridPager
                isFetching={isLoadingIdentifiers}
                onNavigate={handleNavigateIdentifiersPage}
                pagination={pagination}
              />
            }
          />
        </Box>
      )}
    </Box>
  );
});

export { IDMatching };
